"use client";

import { Text } from "@treebo-noss/superhero-design-system";
import { useEffect } from "react";
import { HeroSection } from "@/components/booking/HeroSection";
import { LoginCard } from "@/components/login/LoginCard";
import { useTenantConfig, useTenantProfile } from "@/context/TenantContext";
import { loginScreenViewed } from "@/features/login/analytics";
import type { Hotel } from "@/graphql/hotel";
import { useAnalyticsContext } from "@/lib/analytics/context";
import { GUEST_CONNECT_CONFIGS } from "@/lib/tenant";
import { getHeroImageSrc } from "@/lib/utils/hotelUtils";

interface LoginScreenProps {
  hotelId: string;
  hotelDetails?: Hotel;
  isBookingNotFound?: boolean;
}

export const LoginScreen = ({
  hotelId,
  hotelDetails,
  isBookingNotFound = false,
}: LoginScreenProps) => {
  const clearContext = useAnalyticsContext((s) => s.clearContext);
  const setAnalyticsContext = useAnalyticsContext((s) => s.setContext);

  useEffect(() => {
    clearContext();
    loginScreenViewed();
  }, [clearContext]);

  useEffect(() => {
    if (!hotelDetails) return;
    setAnalyticsContext({
      hotelId,
      hotelName: hotelDetails.name?.newName,
      city: hotelDetails.location?.city,
    });
  }, [hotelDetails, hotelId, setAnalyticsContext]);

  const heroImageSrc = getHeroImageSrc(
    hotelDetails?.propertyImages,
    "login-screen.jpg",
  );

  const tenantProfile = useTenantProfile();
  const { value: tenantBrandDisplayName } = useTenantConfig(
    GUEST_CONNECT_CONFIGS.GC_BRAND_DISPLAY_NAME,
  );

  const heroTitle =
    hotelDetails?.name?.newName ??
    (tenantBrandDisplayName as string | undefined) ??
    tenantProfile?.defaultBrandDisplayName;

  const heroSectionText = heroTitle ? (
    <div>
      <Text size="l" as="p" weight="medium" className="text-white mb-3">
        Welcome to
      </Text>
      <Text size="4xl" as="p" weight="semibold" className="text-white">
        {heroTitle}
      </Text>
    </div>
  ) : null;

  return (
    <div className="flex flex-col min-h-screen">
      <HeroSection
        imageSrc={heroImageSrc}
        heroText={heroSectionText}
        locationLink={hotelDetails?.location?.mapsLink ?? ""}
        phoneNumber={hotelDetails?.phoneNumber ?? ""}
      >
        <LoginCard isBookingNotFound={isBookingNotFound} />
      </HeroSection>

      <div className="bg-neutral-100 px-4 py-9 flex flex-col gap-6 flex-1">
        <div>
          <Text
            as="p"
            className="text-neutral-700 mb-2"
            weight="semibold"
            size="m"
          >
            Welcome to Your Stay Experience!
          </Text>
          <Text as="p" className="text-neutral-700" size="xs">
            Manage your booking, complete check-in, and access everything you
            need for a smooth and comfortable stay.
          </Text>
        </div>
      </div>
    </div>
  );
};
